/**
 * Workflow queue aligned with Task Manager statuses and delivery types.
 * Lanes: New Orders, Delivery Changes, Due Today, Exceptions, Completed Today.
 * Pickup-change tasks cover Kiosk, Counter, Curbside, Bedside, Courier, Mail Order.
 */

export const PICKUP_CHANGE_TASK_TYPE_ID =
  "7a84fdc7-81b8-4b43-8a18-e5183e7b7f60";
export const MISSED_CONSULT_TASK_TYPE_ID =
  "bc3571b8-9b76-4239-a949-a1c57b54e14f";
export const DELIVERY_CHANGE_SLA_MS = 10 * 60 * 1000;

export const OPEN_QUEUE_STATUSES = ["Open", "InProgress"];

export const DELIVERY_UPDATE_TASK_TYPE_ID =
  "1344f9bc-8919-4a4a-ad23-79e73828b129";
export const REFILL_REQUEST_TASK_TYPE_ID =
  "b09ce90d-c022-4555-bdf8-1f2e2146bf8b";
export const MARKETPLACE_STOCK_TASK_TYPE_ID =
  "a7c4e2b1-9d30-4f58-8c16-2b6e0a91d4f3";
export const APPOINTMENT_REQUEST_TASK_TYPE_ID =
  "36bacb5e-c78b-4f7b-9276-a9b32c81849d";

export var QUEUE_LANES = [
  "new_orders",
  "delivery_change",
  "due_today",
  "exceptions",
  "completed",
];
 
var LEGACY_LANES = {
  ready_to_stage: "delivery_change",
  ready_to_fill: "due_today",
  out_for_delivery: "new_orders",
};

var METHOD_ALIASES = {
  kiosk: "kiosk",
  locker: "kiosk",
  counter: "counter",
  curbside: "curbside",
  bedside: "bedside",
  "meds to beds": "bedside",
  "meds-to-beds": "bedside",
  courier: "courier",
  "mail order": "mail_order",
  mail_order: "mail_order",
  other: "other",
};

export var METHOD_CHIP_SX = {
  kiosk: { backgroundColor: "#1e293b", color: "#fff" },
  counter: { backgroundColor: "#0f766e", color: "#fff" },
  curbside: { backgroundColor: "#ea580c", color: "#fff" },
  bedside: { backgroundColor: "#6d28d9", color: "#fff" },
  courier: { backgroundColor: "#64748b", color: "#fff" },
  mail_order: { backgroundColor: "#0369a1", color: "#fff" },
  other: { backgroundColor: "#475569", color: "#fff" },
};

function str(value) {
  return value == null ? "" : String(value);
}

function optionalStr(value) {
  var next = str(value).trim();
  return next ? next : null;
}

export function emptyQueueSummary() {
  return {
    total: 0,
    urgent: 0,
    new_orders: 0,
    delivery_change: 0,
    due_today: 0,
    exceptions: 0,
    completed: 0,
    deliveryChange: 0,
    exception: 0,
    dueToday: 0,
  };
}

export function normalizeLane(lane) {
  var value = String(lane || "").trim();
  if (!value) return "";
  return LEGACY_LANES[value] || value;
}

export function parsePickupDetails(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw === "object") return raw;
  if (typeof raw !== "string") return null;
  try {
    var parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") return parsed;
  } catch (e) {
    return null;
  }
  return null;
}

export function methodFromLabel(value) {
  if (!value) return null;
  var normalized = String(value).trim().toLowerCase();
  if (!normalized || normalized === "n/a") return null;
  var aliases = Object.keys(METHOD_ALIASES);
  for (var i = 0; i < aliases.length; i += 1) {
    if (normalized.indexOf(aliases[i]) !== -1) {
      return METHOD_ALIASES[aliases[i]];
    }
  }
  return null;
}

export function methodFromAction(action) {
  var value = (action || "").toUpperCase();
  if (value.indexOf("TO_KIOSK") !== -1) return { from: null, to: "kiosk" };
  if (value.indexOf("TO_COUNTER") !== -1) return { from: null, to: "counter" };
  if (value.indexOf("TO_CURBSIDE") !== -1) return { from: null, to: "curbside" };
  if (value.indexOf("TO_BEDSIDE") !== -1) return { from: null, to: "bedside" };
  if (value.indexOf("TO_COURIER") !== -1) return { from: null, to: "courier" };
  if (value.indexOf("TO_MAIL_ORDER") !== -1) return { from: null, to: "mail_order" };
  if (value.indexOf("TO_OTHER") !== -1) return { from: null, to: "other" };
  return { from: null, to: null };
}

export function isDueToday(dueDate, now) {
  if (!dueDate) return false;
  var due = new Date(dueDate);
  if (isNaN(due.getTime())) return false;
  var current = now || new Date();
  return (
    (due.getFullYear() === current.getFullYear() &&
      due.getMonth() === current.getMonth() &&
      due.getDate() === current.getDate()) ||
    due.getTime() < current.getTime()
  );
}

function isMissedConsult(typeId, haystack) {
  return (
    typeId === MISSED_CONSULT_TASK_TYPE_ID ||
    haystack.indexOf("missed consultation") !== -1 ||
    haystack.indexOf("missed consult") !== -1
  );
}

function isRefill(typeId, haystack) {
  return (
    typeId === REFILL_REQUEST_TASK_TYPE_ID || haystack.indexOf("refill") !== -1
  );
}

function isDeliveryUpdate(typeId, haystack) {
  return (
    typeId === DELIVERY_UPDATE_TASK_TYPE_ID ||
    haystack.indexOf("delivery update") !== -1
  );
}

var LANE_TYPE_IDS = {
  new_orders: [DELIVERY_UPDATE_TASK_TYPE_ID, MARKETPLACE_STOCK_TASK_TYPE_ID],
  delivery_change: [PICKUP_CHANGE_TASK_TYPE_ID],
  due_today: [
    REFILL_REQUEST_TASK_TYPE_ID,
    APPOINTMENT_REQUEST_TASK_TYPE_ID,
    DELIVERY_UPDATE_TASK_TYPE_ID,
    MARKETPLACE_STOCK_TASK_TYPE_ID,
  ],
};

var EMPTY_LANE_TYPE_ID = "00000000-0000-0000-0000-000000000000";

function typeHaystack(input) {
  return (
    str(input && input.taskTypeName) +
    " " +
    str(input && input.title) +
    " " +
    str(input && input.taskName)
  ).toLowerCase();
}

export function classifyLane(input) {
  var type = classifyQueueType(input);
  var typeId = String((input && input.taskTypeId) || "").toLowerCase();
  var haystack = typeHaystack(input);
  var status = String((input && input.status) || "").toLowerCase();
  if (isMissedConsult(typeId, haystack) || type === "missed_consult") return null;
  if (status === "cancelled") return null;
  if (status === "complete" || status === "completed") return "completed";
  if (typeId === MARKETPLACE_STOCK_TASK_TYPE_ID) {
    if (isDueToday(input && input.dueDate, input && input.now)) return "due_today";
    return "new_orders";
  }
  if (type === "exception") return "exceptions";
  if (type === "delivery_change") return "delivery_change";
  if (isRefill(typeId, haystack)) {
    if (isDueToday(input && input.dueDate, input && input.now)) return "due_today";
    return null;
  }
  if (type === "due_today") return "due_today";
  if (isDeliveryUpdate(typeId, haystack)) return "new_orders";
  return null;
}

export function taskTypeIdsForLane(lane, allTypes) {
  var normalized = normalizeLane(lane);
  if (normalized === "completed") return [];
  if (normalized === "exceptions") {
    return [EMPTY_LANE_TYPE_ID];
  }
  if (LANE_TYPE_IDS[normalized]) {
    return LANE_TYPE_IDS[normalized].slice();
  }
  return [];
}

export function statusListForLane(lane) {
  var normalized = normalizeLane(lane);
  if (!normalized) return [];
  if (normalized === "completed") return ["Complete"];
  return ["Open", "InProgress"];
}

export function applyLaneToFilterBody(body, lane, allTypes) {
  if (!body) return body;
  var normalized = normalizeLane(lane);
  if (!normalized) {
    body.taskTypeList = [];
    return body;
  }
  body.taskTypeList = taskTypeIdsForLane(normalized, allTypes);
  return body;
}

export function classifyQueueType(input) {
  var typeId = String((input && input.taskTypeId) || "").toLowerCase();
  var haystack = typeHaystack(input);
  if (
    typeId === PICKUP_CHANGE_TASK_TYPE_ID ||
    haystack.indexOf("pickup change") !== -1 ||
    haystack.indexOf("delivery change") !== -1 ||
    haystack.indexOf("change pickup") !== -1
  ) {
    return "delivery_change";
  }
  if (isMissedConsult(typeId, haystack)) {
    return "missed_consult";
  }
  if (
    haystack.indexOf("exception") !== -1 ||
    haystack.indexOf("failed delivery") !== -1 ||
    haystack.indexOf("failed pickup") !== -1
  ) {
    return "exception";
  }
  if (isDueToday(input && input.dueDate, input && input.now)) return "due_today";
  return "assigned";
}

export function isAssignedToViewer(item, viewer) {
  var viewerId = viewer && viewer.id ? String(viewer.id).trim() : "";
  var viewerName = viewer && viewer.name ? String(viewer.name).trim().toLowerCase() : "";
  if (viewerId && item.assigneeId && viewerId === item.assigneeId) return true;
  var assigneeName = (item.assigneeName || "").trim().toLowerCase();
  if (viewerName && assigneeName && assigneeName === viewerName) return true;
  return false;
}

export function isUnassigned(item) {
  return !item.assigneeId && !(item.assigneeName || "").trim();
}

export function isVisibleInMyQueue(item, viewer) {
  if (isAssignedToViewer(item, viewer)) return true;
  return (
    isUnassigned(item) &&
    (item.type === "delivery_change" || item.type === "exception")
  );
}

export function isUrgentItem(item) {
  if (item.type !== "delivery_change" && item.type !== "exception") return false;
  return item.ageMs >= DELIVERY_CHANGE_SLA_MS;
}

export function formatQueueAge(ageMs) {
  var minutes = Math.max(0, Math.floor(ageMs / 60000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return minutes + " min ago";
  var hours = Math.floor(minutes / 60);
  if (hours < 24) return hours + "h ago";
  return Math.floor(hours / 24) + "d ago";
}

export function methodLabel(method) {
  if (!method) return "";
  if (method === "mail_order") return "Mail Order";
  return method.charAt(0).toUpperCase() + method.slice(1);
}

export function mapQueueWorkItem(row, now) {
  var current = now || new Date();
  var id = optionalStr(row.id || row.task_id);
  if (!id) return null;

  var details = parsePickupDetails(row.task_details || row.TaskDetails) || {};
  var action = optionalStr(row.action || row.Action || details.action);
  var fromAction = methodFromAction(action);
  var changeParts = details.requested_change
    ? String(details.requested_change).split(/\s+to\s+/i)
    : [];
  var fromMethod =
    methodFromLabel(changeParts[0]) ||
    methodFromLabel(details.from_location_type) ||
    fromAction.from;
  var toMethod =
    methodFromLabel(changeParts[1]) ||
    methodFromLabel(details.to_location_type) ||
    methodFromLabel(details.to_location) ||
    fromAction.to;
  var requestedChange = optionalStr(details.requested_change);
  if (!requestedChange && (fromMethod || toMethod)) {
    requestedChange =
      (fromMethod ? methodLabel(fromMethod) : "Current") +
      " → " +
      (toMethod ? methodLabel(toMethod) : "Requested");
  }

  var taskTypeId = optionalStr(
    row.task_type_id || row.TaskTypeID || row.taskTypeId
  );
  var taskTypeName = str(
    row.taskType || row.task_type_name || row.TaskType || row.task_type
  );
  var taskName = str(row.task_name || row.task_title || row.TaskTitle || row.title);
  var dueDate = optionalStr(row.due_date || row.DueDate);
  var type = classifyQueueType({
    taskTypeId: taskTypeId,
    taskTypeName: taskTypeName,
    title: taskName,
    dueDate: dueDate,
    now: current,
  });

  var createdAt = optionalStr(row.created_on || row.CreatedOn || row.createdAt);
  var createdMs = createdAt ? new Date(createdAt).getTime() : current.getTime();
  if (isNaN(createdMs)) createdMs = current.getTime();
  var ageMs = Math.max(0, current.getTime() - createdMs);

  var stock = optionalStr(details.stock_code);
  var pickup = optionalStr(details.pickup_code);
  var bagNumber = optionalStr(details.bag_number);
  var orderLabel =
    (stock && stock !== "N/A" ? stock : null) ||
    (pickup && pickup !== "N/A" ? pickup : null) ||
    optionalStr(row.order_code);

  var item = {
    id: id,
    title: requestedChange || taskName,
    type: type,
    workflowStatus: str(row.status || row.Status || "Open"),
    priority: str(row.priority || row.Priority),
    patientName:
      optionalStr(details.customer_name) ||
      str(row.patientName || row.patient_name || row.Patient),
    assigneeId: optionalStr(row.user_id || row.UserID || row.assigneeId),
    assigneeName: str(
      row.userName || row.user_name || row.User || row.assigneeName
    ),
    method: toMethod || fromMethod,
    fromMethod: fromMethod,
    toMethod: toMethod,
    requestedChange: requestedChange,
    fromLocation: optionalStr(details.from_location),
    toLocation: optionalStr(details.to_location),
    locationName: optionalStr(details.to_location || details.from_location),
    pickupCode: pickup && pickup !== "N/A" ? pickup : null,
    stockCode: stock && stock !== "N/A" ? stock : null,
    bagNumber: bagNumber && bagNumber !== "N/A" ? bagNumber : null,
    source: optionalStr(details.source),
    createdAt: createdAt,
    dueDate: dueDate,
    ageMs: ageMs,
    urgent: false,
    orderLabel: orderLabel,
    lane: "new_orders",
  };
  item.urgent = isUrgentItem(item);
  item.lane = classifyLane({
    taskTypeId: taskTypeId,
    taskTypeName: taskTypeName,
    title: taskName,
    dueDate: dueDate,
    now: current,
    status: item.workflowStatus,
  });
  if (!item.lane || type === "missed_consult") return null;
  return item;
}

export function summarizeLanes(items) {
  var list = items || [];
  var summary = emptyQueueSummary();
  summary.total = list.length;
  summary.urgent = list.filter(function (item) {
    return item.urgent;
  }).length;
  list.forEach(function (item) {
    if (summary[item.lane] != null) summary[item.lane] += 1;
    if (item.type === "delivery_change") summary.deliveryChange += 1;
    if (item.type === "exception") summary.exception += 1;
    if (item.type === "due_today") summary.dueToday += 1;
  });
  return summary;
}

export function summarizeQueue(items) {
  return summarizeLanes(items);
}

export function filterQueueByTab(items, tab) {
  var normalized = normalizeLane(tab);
  if (!normalized || normalized === "all") return items || [];
  return (items || []).filter(function (item) {
    return item.lane === normalized || item.type === normalized;
  });
}

export function filterQueueByLane(items, lane) {
  var normalized = normalizeLane(lane);
  if (!normalized || normalized === "all") return items || [];
  return (items || []).filter(function (item) {
    return item.lane === normalized;
  });
}

export function viewerFromUserData(userData) {
  var user = userData && userData[0];
  if (!user) return viewerFromSession();
  return { id: user.id || null, name: user.name || null };
}

export function persistQueueViewer(userData) {
  var user = userData && userData[0];
  if (!user) return;
  if (user.id) sessionStorage.setItem("userId", String(user.id));
  if (user.name) sessionStorage.setItem("userName", String(user.name));
}

export function viewerFromSession() {
  var id = null;
  var name = null;
  try {
    id = sessionStorage.getItem("userId");
    name = sessionStorage.getItem("userName");
  } catch (e) {
    return { id: null, name: null };
  }
  return { id: id || null, name: name || null };
}

export function taskManagerHref(taskId, type, lane) {
  var href = "/home/taskmanager";
  var params = [];
  if (taskId) params.push("taskId=" + encodeURIComponent(taskId));
  var laneId = normalizeLane(lane);
  if (!laneId && type === "delivery_change") laneId = "delivery_change";
  if (!laneId && type === "exception") laneId = "exceptions";
  if (!laneId && type === "due_today") laneId = "due_today";
  if (laneId) params.push("lane=" + encodeURIComponent(laneId));
  if (params.length) href += "?" + params.join("&");
  return href;
}



// WEBPACK FOOTER //
// ./src/components/workflowQueue/queueModel.js